import { useEffect, useRef, useState } from 'react';
import type { Member } from '@/types';

interface MemberActionsMenuProps {
  member: Member;
  onEditRole: (member: Member) => void;
  onSetPassword: (member: Member) => void;
  onResendInvite: (member: Member) => void;
  onRemove: (member: Member) => void;
}

export default function MemberActionsMenu({
  member,
  onEditRole,
  onSetPassword,
  onResendInvite,
  onRemove,
}: MemberActionsMenuProps) {
  const [open, setOpen] = useState(false);
  // Меню позиционируется fixed по кнопке: таблица участников скроллится
  // и обычная выпадашка обрезалась её контейнером.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen(!open);
  };

  const itemClass =
    'w-full text-left px-3 py-2 text-sm text-foreground-700 hover:bg-background-50 flex items-center gap-2 transition-colors cursor-pointer';

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
      >
        <i className="ri-more-2-line" />
      </button>

      {open && pos && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="w-64 bg-white rounded-xl border border-background-200 shadow-lg z-[60] py-1"
        >
          <button
            onClick={() => {
              setOpen(false);
              onEditRole(member);
            }}
            className={itemClass}
          >
            <i className="ri-shield-user-line text-foreground-400" />
            Изменить роль
          </button>

          <button
            onClick={() => {
              setOpen(false);
              onSetPassword(member);
            }}
            className={itemClass}
          >
            <i className="ri-key-2-line text-foreground-400" />
            Задать пароль
          </button>

          <button
            onClick={() => {
              setOpen(false);
              onResendInvite(member);
            }}
            className={itemClass}
          >
            <i className="ri-mail-send-line text-foreground-400" />
            {member.status === 'pending' ? 'Повторить приглашение' : 'Сбросить пароль и выслать письмом'}
          </button>

          <div className="border-t border-background-100 my-1" />

          <button
            onClick={() => {
              setOpen(false);
              onRemove(member);
            }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors cursor-pointer"
          >
            <i className="ri-user-unfollow-line" />
            Удалить из команды
          </button>
        </div>
      )}
    </>
  );
}
